import React, { useState, useRef, useMemo } from 'react';
import { Card, Button, AgGridTable, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiList, FiSearch, FiX, FiDownload } from 'react-icons/fi';
import ProjectStatusRenderer from 'components/app/crm/project/component/ProjectStatusRenderer';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [date] = String(iso).split('T');
  const [y, m, d] = date.split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

// Server-side searchable / sortable / paginated records grid with Excel export.
// Column `field` names match the backend DETAILED_SORT_COLUMNS whitelist.
const QuotationDetailedTable = ({
  rows, totalRows, page, perPage, loading, error,
  search, onPageChange, onPerPageChange, onSearchChange, onSortChange, onExport, exportLoading,
}) => {
  const [searchInput, setSearchInput] = useState(search || '');
  const searchTimer = useRef(null);
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const handleSearchInput = (value) => {
    setSearchInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearchChange(value), 400);
  };

  const columnDefs = useMemo(() => ([
    {
      // Pinned columns must opt out of the grid-wide flex sizing (flex:1).
      headerName: 'Quote #', field: 'q_number', width: 150, minWidth: 130, flex: 0, pinned: 'left',
      cellRenderer: (p) => (
        <span className="tw-inline-flex tw-items-center">
          {p.data?.project_id ? (
            <a href={`/crm/projects/${p.data.project_id}`} target="_blank" rel="noreferrer"
              className="tw-font-mono tw-font-bold tw-text-yellow-600 hover:tw-underline">{p.value || '—'}</a>
          ) : <span className="tw-font-mono tw-font-bold tw-text-gray-700">{p.value || '—'}</span>}
          {p.data?.became_job && (
            <span className="tw-ml-1.5 tw-text-[10px] tw-leading-none tw-font-bold tw-px-1.5 tw-py-1 tw-rounded-xl tw-bg-emerald-100 tw-text-emerald-600">JOB</span>
          )}
        </span>
      ),
    },
    {
      headerName: 'Job #', field: 'j_number', width: 140, minWidth: 120, flex: 0,
      cellRenderer: (p) => (
        p.value
          ? <span className="tw-font-mono tw-font-semibold tw-text-emerald-600">{p.value}</span>
          : <span className="tw-text-gray-300">—</span>
      ),
    },
    {
      headerName: 'Status', field: 'status_label', minWidth: 150, sortable: false,
      cellRenderer: (p) => <ProjectStatusRenderer value={p.value} data={p.data} />,
    },
    {
      headerName: 'Buildings', field: 'building_count', minWidth: 90, type: 'numericColumn',
      tooltipField: 'building_names', cellClass: 'tw-tabular-nums tw-text-center',
      cellRenderer: (p) => (
        <span title={p.data?.building_names} className="tw-cursor-help">
          {p.value} {Number(p.value) === 1 ? 'bldg' : 'bldgs'}
        </span>
      ),
    },
    {
      headerName: 'Building Names', field: 'building_names', minWidth: 160, sortable: false,
      tooltipField: 'building_names', valueFormatter: (p) => p.value || '—',
    },
    { headerName: 'Project', field: 'project_name', minWidth: 200, tooltipField: 'project_name',
      cellRenderer: (p) => (
        p.data?.project_id
          ? <Link to={`/crm/projects/${p.data.project_id}`} className="tw-text-blue-600 hover:tw-underline">{p.value || '—'}</Link>
          : <span className="tw-text-gray-700">{p.value || '—'}</span>
      ),
    },
    { headerName: 'Customer', field: 'customer_name', minWidth: 180, valueFormatter: (p) => p.value || '—' },
    {
      headerName: 'Weight (M.T)', field: 'weight', minWidth: 120,
      valueFormatter: (p) => fmtW(p.value), cellClass: 'tw-tabular-nums tw-text-right',
    },
    { headerName: 'Sales Incharge', field: 'sales_incharge', minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Sales Unit',     field: 'unit_name',      minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Region',         field: 'region',         minWidth: 120, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Country',        field: 'country',        minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Province',       field: 'province',       minWidth: 140, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Entry Date',     field: 'quotation_entry_date', minWidth: 120, valueFormatter: (p) => fmtDate(p.value) },
  ]), []);

  const handleSortChanged = ({ colId, sort }) => {
    if (!colId || !sort) return;
    onSortChange(colId, sort === 'asc' ? 'ASC' : 'DESC');
  };

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <FiList size={15} className="tw-text-primary-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Quotation Records</h3>
        <span className="tw-text-[10px] tw-text-gray-400">{fmt(totalRows)} records</span>

        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Search Q# / J# / project / customer…"
              value={searchInput}
              onChange={e => handleSearchInput(e.target.value)}
              className="tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-xs tw-w-64 tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400"
            />
            {searchInput && (
              <button type="button" onClick={() => handleSearchInput('')}
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer">
                <FiX size={12} />
              </button>
            )}
          </div>

          <Button variant="secondary" size="sm" leftIcon={FiDownload} loading={exportLoading} onClick={onExport}
            className="tw-text-emerald-700 hover:tw-bg-emerald-50 hover:tw-border-emerald-300">
            Export Excel
          </Button>
        </div>
      </div>

      {error ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">Failed to load records</p>
      ) : (
        <div className="tw-p-3">
          <AgGridTable
            rowData={rows}
            columnDefs={columnDefs}
            loading={loading}
            pagination={false}
            filter={false}
            sort
            rowHeight={38}
            height="480px"
            onSortChanged={handleSortChanged}
            enableCellTextSelection
          />
          <Pagination
            className="tw-mt-3"
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPerPageChange}
            pageSizeOptions={[25, 50, 100, 200]}
            disabled={loading}
          />
        </div>
      )}
    </Card>
  );
};

QuotationDetailedTable.propTypes = {
  rows: PropTypes.array, totalRows: PropTypes.number, page: PropTypes.number,
  perPage: PropTypes.number, loading: PropTypes.bool, error: PropTypes.any,
  search: PropTypes.string,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
};
QuotationDetailedTable.defaultProps = {
  rows: [], totalRows: 0, page: 1, perPage: 50, loading: false, error: null,
  search: '', exportLoading: false,
};

export default QuotationDetailedTable;
